import { useCallback, useMemo, useState, type ComponentType } from "react"
import moment, { type Moment } from "moment"
import { Chip, Popover } from "@mui/material"
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material"
import PeriodSelectorOptionsUntyped from "components/period-selector/options"
import PeriodDisplay, { type PeriodLabel } from "connected-components/dashboard/components/period-display"
import OPTooltip from "components/material/tooltip"
import { PERIOD_OPTIONS, CUSTOM_PERIOD, ONE_DAY, MONTH_TO_DATE, ONE_MONTH, YEAR_TO_DATE, getDriftStartAndEndDates, type Period } from "helpers/dates"
import type { IsoDate } from "helpers/types/dates"
import { computeDateOverride, formatDateRangeLabel, type DateOverride } from "../../helpers/date-override"
import "./style.scss"

// components/period-selector/options.jsx is untyped and calls endDate.format(...) unconditionally,
// so endDate must be a Moment despite its PropTypes declaring a string. Needs `as unknown as` (not
// a direct `as ComponentType` like risk/header.tsx's ListItemDropDown cast) since the inferred JS
// prop shape doesn't overlap enough with this interface for a direct cast to compile.
interface PeriodSelectorOptionsProps {
  period: string
  customizeOptions?: (options: { value: Period, label: string, disabled?: boolean, disabledReason?: string }[]) => { value: Period, label: string, disabled?: boolean, disabledReason?: string }[]
  handleClickAway: () => void
  handleSelect: (period: Period) => void
  endDate: Moment
  latestDate: string
}
const PeriodSelectorOptions = PeriodSelectorOptionsUntyped as unknown as ComponentType<PeriodSelectorOptionsProps>

// No custom date selection for MVP — presets only.
const PANEL_PERIOD_OPTIONS = PERIOD_OPTIONS.filter(({ value }) => value !== CUSTOM_PERIOD)
// Stable reference so PeriodSelectorOptions' useMemo([customizeOptions]) actually memoizes.
const getPanelPeriodOptions = () => PANEL_PERIOD_OPTIONS

// getDriftStartAndEndDates floors these to the same trailing 3-month query as 3M, so picking
// between them changes nothing — disable rather than offer indistinguishable choices. YTD joins
// this list dynamically (see driftFloorOptions below) since whether it's under 3 months depends
// on the calendar date, unlike these three which always are.
const DRIFT_FLOOR_PERIODS: Period[] = [ONE_DAY, MONTH_TO_DATE, ONE_MONTH]
const DRIFT_FLOOR_PERIODS_WITH_YTD: Period[] = [...DRIFT_FLOOR_PERIODS, YEAR_TO_DATE]
const DRIFT_FLOOR_TOOLTIP = "Drift requires at least 3 months of history for a statistically meaningful calculation, so periods under 3 months aren't selectable here."

interface PanelPeriodControlDates {
  earliestDate: IsoDate | null
  latestDate: IsoDate | null
  startDate: IsoDate
  endDate: IsoDate
}

interface PanelPeriodControlProps {
  dates: PanelPeriodControlDates
  dateOverride: DateOverride | null
  isDateOverrideActive: boolean
  setDateOverride: (dateOverride: DateOverride) => void
  clearDateOverride: () => void
  isPerformance?: boolean
  // Factors' Risk tab only; every other caller omits both, keeping today's plain "Period" behavior.
  driftDates?: { startDate: IsoDate, endDate: IsoDate }
  label?: PeriodLabel
}

export type { PeriodLabel }

// Each module (graph/components/panel-period-control.tsx, and KPI/Factors/Asset View's own
// wrappers) calls its own data hook and passes the result in, so this component stays hook-agnostic.
export default function PanelPeriodControl({
  dates: { earliestDate, latestDate, startDate, endDate },
  dateOverride,
  isDateOverrideActive,
  setDateOverride,
  clearDateOverride,
  isPerformance = true,
  driftDates,
  label = "Period",
}: PanelPeriodControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const datesReady = earliestDate != null && latestDate != null

  // earliestDate/latestDate load asynchronously, independent of whatever gates the panel's render.
  // setOpen(!open) (not a functional update) matches PeriodSelector's handleToggle — a functional
  // update fights the dropdown's ClickAwayListener, which also setOpen(false)s on the same click.
  const handleToggle = useCallback(() => {
    if (!datesReady) return
    setOpen(!open)
  }, [datesReady, open])
  const handleClickAway = useCallback(() => setOpen(false), [])

  const handleSelect = useCallback((period: Period) => {
    setOpen(false)
    const computed = computeDateOverride(period, earliestDate, latestDate)
    if (computed) setDateOverride(computed)
  }, [setDateOverride, earliestDate, latestDate])

  // YTD is only sometimes under 3 months (roughly Jan-Mar), so whether it floors to the same
  // window as 3M depends on today's date, not just the period itself like DRIFT_FLOOR_PERIODS.
  // Floor and YTD must both anchor on latestDate (not driftDates.startDate, which floors off
  // whatever endDate the panel currently shows) since selecting either one re-anchors there too.
  const driftFloorOptions = useMemo(() => {
    if (!driftDates || earliestDate == null || latestDate == null) return null
    const latestDateFloor = getDriftStartAndEndDates(latestDate, earliestDate).startDate
    const ytdStartDate = computeDateOverride(YEAR_TO_DATE, earliestDate, latestDate)?.startDate
    const disabledPeriods = ytdStartDate != null && ytdStartDate >= latestDateFloor
      ? DRIFT_FLOOR_PERIODS_WITH_YTD
      : DRIFT_FLOOR_PERIODS
    return PANEL_PERIOD_OPTIONS.map((option) =>
      disabledPeriods.includes(option.value)
        ? { ...option, disabled: true, disabledReason: DRIFT_FLOOR_TOOLTIP }
        : option,
    )
  }, [driftDates, earliestDate, latestDate])
  const getDriftFloorOptions = useCallback(() => driftFloorOptions ?? PANEL_PERIOD_OPTIONS, [driftFloorOptions])

  // open && latestDate != null (not an always-mounted Popover) so a second trigger click removes this synchronously, and a mid-open refetch that nulls latestDate never reaches PeriodSelectorOptions.
  // Popover portals this out of the panel's own stacking context so it isn't clipped by a sibling panel (same fix as components/period-selector/index.jsx).
  const options = open && latestDate != null && (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClickAway}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { elevation: 0, className: "period-selector-popover-paper" } }}
    >
      <PeriodSelectorOptions
        period={dateOverride?.period ?? CUSTOM_PERIOD}
        customizeOptions={driftDates ? getDriftFloorOptions : getPanelPeriodOptions}
        handleClickAway={handleClickAway}
        handleSelect={handleSelect}
        endDate={moment(latestDate)}
        latestDate={latestDate}
      />
    </Popover>
  )

  if (dateOverride) {
    // isDateOverrideActive false means the stored override fell back to dashboard dates (boundaries
    // not loaded yet, or a collapsed fixed-length window) — keep the chip (and its × ) so the stored
    // override stays clearable, but don't claim it's applied when the panel is using dashboard dates.
    return (
      <div className={`components_period-selector panel-period-control panel-period-control--active${isDateOverrideActive ? "" : " panel-period-control--inactive"}`}>
        <span className="panel-period-control__label">{label}</span>
        <OPTooltip
          title={isDateOverrideActive
            ? "Click to change period, or use the × to revert to dashboard dates"
            : "This period isn't applied — showing dashboard dates. Use the × to clear it."}
          placement="top"
        >
          <Chip
            ref={setAnchorEl}
            className="panel-period-control__chip"
            size="small"
            // startDate/endDate (not dateOverride.startDate/endDate) — these are resolveDateOverride's
            // clamped values, so the chip can't show a wider range than what the panel actually queries.
            label={`${dateOverride.period}: ${formatDateRangeLabel(startDate, endDate)}`}
            onClick={handleToggle}
            onDelete={clearDateOverride}
          />
        </OPTooltip>
        {options}
      </div>
    )
  }

  return (
    <div className="components_period-selector panel-period-control">
      <span className="panel-period-control__label">{label}</span>
      <a className="link panel-period-control__button" ref={setAnchorEl} onClick={handleToggle}>
        <PeriodDisplay isPerformance={isPerformance} driftDates={driftDates} hideLabel />
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
      </a>
      {options}
    </div>
  )
}
